import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchHierarchyTree,
  type HierarchyTreeNode,
} from '@/api/productHierarchy'
import { queryKeys } from '@/lib/queryKeys'
import type { DropdownOptionRead } from '@/types/dag'

const STALE_TIME = 10 * 60 * 1000

function nodeToDisplayValue(node: HierarchyTreeNode): string {
  return node.name
}

function nodesToDropdownOptions(
  nodes: HierarchyTreeNode[]
): DropdownOptionRead[] {
  return nodes.map(n => ({
    value: nodeToDisplayValue(n),
    display_label: nodeToDisplayValue(n),
  }))
}

export interface HierarchyForDagData {
  divisionOptions: DropdownOptionRead[]
  departmentOptions: DropdownOptionRead[]
  classOptions: DropdownOptionRead[]
  subclassOptions: DropdownOptionRead[]
  getDepartmentOptions: (
    divisionDisplayValue: string | null
  ) => DropdownOptionRead[]
  getClassOptions: (
    departmentDisplayValue: string | null
  ) => DropdownOptionRead[]
  getSubclassOptions: (classDisplayValue: string | null) => DropdownOptionRead[]
}

/** Dedupes options by value, preserving first-seen order. */
function dedupeOptions(options: DropdownOptionRead[]): DropdownOptionRead[] {
  const seen = new Set<string>()
  const result: DropdownOptionRead[] = []
  for (const option of options) {
    if (seen.has(option.value)) continue
    seen.add(option.value)
    result.push(option)
  }
  return result
}

/**
 * Hook that loads the full hierarchy tree and provides lookup functions
 * for the DAG table's cascading department → class → subclass dropdowns.
 */
export function useHierarchyForDag(): HierarchyForDagData | null {
  const { data: tree } = useQuery({
    queryKey: queryKeys.hierarchy.tree(),
    queryFn: fetchHierarchyTree,
    staleTime: STALE_TIME,
  })

  return useMemo(() => {
    if (!tree) return null

    // Build a reverse map: "code - name" → HierarchyTreeNode (with children)
    const displayMap = new Map<string, HierarchyTreeNode>()

    // Flat (non-cascading) node lists per level, for whole-tree option lists
    // such as filter-builder dropdowns that aren't scoped to a single parent.
    const flatByLevel: Record<HierarchyTreeNode['level'], HierarchyTreeNode[]> =
      {
        DIVISION: [],
        DEPARTMENT: [],
        CLASS: [],
        SUBCLASS: [],
      }

    function indexTree(nodes: HierarchyTreeNode[]) {
      for (const node of nodes) {
        displayMap.set(nodeToDisplayValue(node), node)
        // Also index by name alone and code alone as fallback
        displayMap.set(node.name, node)
        displayMap.set(node.code, node)
        flatByLevel[node.level].push(node)
        if (node.children) indexTree(node.children)
      }
    }
    indexTree(tree)

    const divisionOptions = dedupeOptions(
      nodesToDropdownOptions(flatByLevel.DIVISION)
    )
    const departmentOptions = dedupeOptions(
      nodesToDropdownOptions(flatByLevel.DEPARTMENT)
    )
    const classOptions = dedupeOptions(
      nodesToDropdownOptions(flatByLevel.CLASS)
    )
    const subclassOptions = dedupeOptions(
      nodesToDropdownOptions(flatByLevel.SUBCLASS)
    )

    function getDepartmentOptions(
      divisionDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!divisionDisplayValue) return []
      const divNode = displayMap.get(divisionDisplayValue)
      if (!divNode || divNode.level !== 'DIVISION') return []
      const depts = (divNode.children ?? []).filter(
        c => c.level === 'DEPARTMENT'
      )
      return nodesToDropdownOptions(depts)
    }

    function getClassOptions(
      departmentDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!departmentDisplayValue) return []
      const deptNode = displayMap.get(departmentDisplayValue)
      if (!deptNode || deptNode.level !== 'DEPARTMENT') return []
      const classes = (deptNode.children ?? []).filter(c => c.level === 'CLASS')
      return nodesToDropdownOptions(classes)
    }

    function getSubclassOptions(
      classDisplayValue: string | null
    ): DropdownOptionRead[] {
      if (!classDisplayValue) return []
      const classNode = displayMap.get(classDisplayValue)
      if (!classNode || classNode.level !== 'CLASS') return []
      const subclasses = (classNode.children ?? []).filter(
        c => c.level === 'SUBCLASS'
      )
      return nodesToDropdownOptions(subclasses)
    }

    return {
      divisionOptions,
      departmentOptions,
      classOptions,
      subclassOptions,
      getDepartmentOptions,
      getClassOptions,
      getSubclassOptions,
    }
  }, [tree])
}
